// Pure, framework-free logic for athlete platform access (see
// `features/athlete-access.md`). Athletes — adults and minors alike — can sign
// in to view and edit their own profile, and see the other members of their
// club. A minor's access is held by a guardian, who may in turn issue the child
// their own login.
//
// This module owns the rules that decide all of that:
//
//   * who reaches which athlete profile, and with what rights
//     (`AthleteAccessLink` and the `canX` helpers);
//   * which profile fields an athlete may change themselves
//     (`validateAthleteSelfEdit` / `applyAthleteSelfEdit`) — a deliberately
//     narrow subset, because the identity fields drive competition categories
//     and the derived age that gates guardianship itself;
//   * what a club member sees of another club member (`toClubMateProfile`),
//     which — like `toPublicAthleteProfile` — is a projection and therefore the
//     single security boundary: a field it does not copy cannot leak;
//   * the invitation challenge (lifetime, single-use, claim URL), mirroring the
//     passwordless-login link in `loginLinks.ts`.
//
// Kept free of React/persistence imports so the client (rendering the athlete
// surface) and the server (enforcing it) share exactly one set of rules.

import {
  ATHLETE_PHOTO_MAX,
  COACH_DESCRIPTION_MAX,
  athleteAge,
} from './athletes';
import type {
  Athlete,
  AthleteAccessLink,
  AthleteAccessRelation,
  AthleteInput,
  AthleteInviteChallenge,
  AthleteLinkView,
  SportId,
} from './types';

/** Every access relation, in display order. */
export const ATHLETE_ACCESS_RELATIONS: readonly AthleteAccessRelation[] = [
  'SELF',
  'GUARDIAN',
];

/** Whether `value` is a known access relation. */
export function isAthleteAccessRelation(
  value: unknown,
): value is AthleteAccessRelation {
  return (
    typeof value === 'string' &&
    (ATHLETE_ACCESS_RELATIONS as readonly string[]).includes(value)
  );
}

/**
 * Age at which an athlete is an adult on the platform. Adulthood is not stored:
 * it is derived from the date of birth on every request, so an athlete becomes
 * an adult the day they turn 18 with no scheduled job involved.
 */
export const ADULT_AGE = 18;

/**
 * Whether the athlete is a minor at `now`. A malformed or missing date of birth
 * counts as a minor — the safe default, since being a minor only ever *adds*
 * guardian oversight rather than removing a restriction.
 */
export function isMinor(dateOfBirth: string, now: number = Date.now()): boolean {
  const age = athleteAge(dateOfBirth, now);
  return age === null || age < ADULT_AGE;
}

/** Whether an access grant is still live (not revoked). */
export function isAccessLive(link: Pick<AthleteAccessLink, 'revokedAt'>): boolean {
  return link.revokedAt === null;
}

/** Only the live grants of a set. Every rule below works on live grants alone. */
export function liveAccessLinks(
  links: readonly AthleteAccessLink[],
): AthleteAccessLink[] {
  return links.filter(isAccessLive);
}

/**
 * The caller's live grant on `personalNumber`, or null when they hold none.
 * A `SELF` grant wins over a `GUARDIAN` one in the (unusual) case both exist,
 * since it is the stronger claim on the profile.
 */
export function findAccessLink(
  links: readonly AthleteAccessLink[],
  personalNumber: string,
): AthleteAccessLink | null {
  const mine = liveAccessLinks(links).filter(
    (link) => link.personalNumber === personalNumber,
  );
  return mine.find((link) => link.relation === 'SELF') ?? mine[0] ?? null;
}

/** Every athlete the caller reaches, in grant order, without duplicates. */
export function accessiblePersonalNumbers(
  links: readonly AthleteAccessLink[],
): string[] {
  const seen = new Set<string>();
  for (const link of liveAccessLinks(links)) {
    seen.add(link.personalNumber);
  }
  return [...seen];
}

/** Whether the caller may read `personalNumber`'s profile at all. */
export function canViewAthlete(
  links: readonly AthleteAccessLink[],
  personalNumber: string,
): boolean {
  return findAccessLink(links, personalNumber) !== null;
}

/**
 * Whether the caller may change `personalNumber`'s self-editable fields. Only
 * ever the narrow subset — `SELF_EDITABLE_ATHLETE_FIELDS` — never the identity
 * fields, which stay club-managed regardless of this returning true.
 */
export function canEditAthlete(
  links: readonly AthleteAccessLink[],
  personalNumber: string,
): boolean {
  return findAccessLink(links, personalNumber)?.canEdit === true;
}

/**
 * Whether the caller may issue the athlete their own login. Held by guardians
 * only, at any age: the guardian decides when their child is ready for an
 * account, and the grant is stamped with `invitedByUserId` so the decision is
 * auditable. An athlete cannot invite themselves (they are already in), and a
 * `SELF` holder cannot mint further accounts on their own profile.
 */
export function canGrantChildAccess(
  links: readonly AthleteAccessLink[],
  personalNumber: string,
): boolean {
  return liveAccessLinks(links).some(
    (link) => link.personalNumber === personalNumber && link.relation === 'GUARDIAN',
  );
}

/**
 * Whether the caller may revoke the grant `target`, given the athlete's date of
 * birth. Three cases, and nothing else:
 *
 *   * a **guardian** of that athlete may revoke a `SELF` grant they issued to
 *     their child, or step down by revoking their own grant;
 *   * an **adult athlete** may revoke any guardian's grant on their own profile
 *     — the point at which oversight becomes theirs to end;
 *   * a **minor athlete** may not revoke their guardian (otherwise the child
 *     could simply lock the parent out).
 *
 * Revoking another guardian's grant is never allowed: guardians are peers.
 */
export function canRevokeAccess(
  callerLinks: readonly AthleteAccessLink[],
  target: AthleteAccessLink,
  athleteDateOfBirth: string,
  now: number = Date.now(),
): boolean {
  if (!isAccessLive(target)) return false;
  const mine = liveAccessLinks(callerLinks).filter(
    (link) => link.personalNumber === target.personalNumber,
  );
  if (mine.length === 0) return false;

  const asGuardian = mine.find((link) => link.relation === 'GUARDIAN');
  if (asGuardian) {
    if (target.relation === 'SELF') return true;
    return target.id === asGuardian.id; // stepping down, not unseating a peer
  }

  const asSelf = mine.find((link) => link.relation === 'SELF');
  if (asSelf && target.relation === 'GUARDIAN') {
    return !isMinor(athleteDateOfBirth, now);
  }
  return false;
}

/**
 * Project a grant into the entry `GET /api/auth/me` returns, resolving the
 * athlete's display name and whether they are still a minor.
 */
export function toAthleteLinkView(
  link: AthleteAccessLink,
  athlete: Pick<Athlete, 'fullName' | 'dateOfBirth'>,
  now: number = Date.now(),
): AthleteLinkView {
  return {
    accessId: link.id,
    personalNumber: link.personalNumber,
    fullName: athlete.fullName,
    relation: link.relation,
    canEdit: link.canEdit,
    isMinor: isMinor(athlete.dateOfBirth, now),
  };
}

// --- What the club's access panel sees ------------------------------------

/**
 * One access grant as shown to club staff on an athlete's access panel. Carries
 * the holder's email so staff can tell *who* holds access (the point of the
 * panel), but never the account's id-adjacent internals beyond what a revoke
 * needs. Revoked grants are listed too, greyed out — the history is the audit
 * trail of who could once see the profile.
 */
export interface AthleteAccessView {
  id: string;
  relation: AthleteAccessRelation;
  canEdit: boolean;
  email: string;
  name: string;
  createdAt: number;
  revokedAt: number | null;
}

/**
 * One pending invitation as shown to club staff. Deliberately built by a
 * projection so `tokenHash` — the only secret material on the row — cannot reach
 * a response by accident: the same discipline as `ListMonkInstanceView` hiding
 * an instance token.
 */
export interface AthleteInviteView {
  id: string;
  email: string;
  relation: AthleteAccessRelation;
  expiresAt: number;
  createdAt: number;
}

/** Project a grant for the staff panel, resolving the holder's account. */
export function toAthleteAccessView(
  link: AthleteAccessLink,
  holder: { email: string; name: string } | null,
): AthleteAccessView {
  return {
    id: link.id,
    relation: link.relation,
    canEdit: link.canEdit,
    email: holder?.email ?? '',
    name: holder?.name ?? '',
    createdAt: link.createdAt,
    revokedAt: link.revokedAt,
  };
}

/** Project a pending invitation for the staff panel, dropping the token hash. */
export function toAthleteInviteView(invite: AthleteInviteChallenge): AthleteInviteView {
  return {
    id: invite.id,
    email: invite.email,
    relation: invite.relation,
    expiresAt: invite.expiresAt,
    createdAt: invite.createdAt,
  };
}

/** An athlete's full access picture, as the club's panel renders it. */
export interface AthleteAccessPanel {
  grants: AthleteAccessView[];
  pending: AthleteInviteView[];
}

// --- Self-service editing -------------------------------------------------

/**
 * The profile fields an athlete (or their guardian) may change themselves.
 * Everything else — full name, gender, date of birth, coach mark, the owning
 * company and the personal number — is club-managed: those fields decide
 * competition categories and the derived age that gates guardianship, so a
 * self-editable date of birth would let a minor edit their way out of
 * oversight. Widening this list is a one-line change here plus a rule in
 * `validateAthleteSelfEdit`.
 */
export const SELF_EDITABLE_ATHLETE_FIELDS = ['photoUrls', 'coachDescription'] as const;

/** A self-service profile edit: any subset of the self-editable fields. */
export interface AthleteSelfEdit {
  photoUrls?: string[];
  /** Free-text bio. Persisted as `coachDescription`; kept only for coaches. */
  coachDescription?: string;
}

/** Per-field validation messages; an absent key means that field is valid. */
export interface AthleteSelfEditErrors {
  photoUrls?: string;
  coachDescription?: string;
}

export interface AthleteSelfEditValidation {
  valid: boolean;
  errors: AthleteSelfEditErrors;
  /** Normalized fields, present only when `valid` — absent keys mean "unchanged". */
  value: AthleteSelfEdit | null;
}

/**
 * Validate a self-service edit. Mirrors the relevant half of `validateAthlete`
 * (the same photo cap and description limit), but every field is optional: an
 * absent key means "leave as is", so the athlete surface can submit a partial
 * form. A bio submitted for a non-coach is accepted and stored — the coach mark
 * only governs whether the club's own form shows the field.
 */
export function validateAthleteSelfEdit(
  patch: AthleteSelfEdit,
): AthleteSelfEditValidation {
  const errors: AthleteSelfEditErrors = {};
  const value: AthleteSelfEdit = {};

  if (patch.photoUrls !== undefined) {
    const photoUrls = patch.photoUrls;
    if (!Array.isArray(photoUrls)) {
      errors.photoUrls = 'Photo entries must be a list of URLs.';
    } else if (photoUrls.length > ATHLETE_PHOTO_MAX) {
      errors.photoUrls = `An athlete can have at most ${ATHLETE_PHOTO_MAX} photos.`;
    } else if (
      photoUrls.some((url) => typeof url !== 'string' || url.trim().length === 0)
    ) {
      errors.photoUrls = 'Photo entries must be non-empty URLs.';
    } else {
      value.photoUrls = photoUrls.map((url) => url.trim());
    }
  }

  if (patch.coachDescription !== undefined) {
    const bio = String(patch.coachDescription).trim();
    if (bio.length > COACH_DESCRIPTION_MAX) {
      errors.coachDescription = `A bio must be ${COACH_DESCRIPTION_MAX} characters or fewer.`;
    } else {
      value.coachDescription = bio;
    }
  }

  const valid = Object.keys(errors).length === 0;
  return { valid, errors, value: valid ? value : null };
}

/**
 * Fold a validated self-edit onto the athlete's current profile, producing the
 * full `AthleteInput` the athlete service persists. Identity fields are always
 * taken from `athlete`, never from the patch, so an unexpected field on the
 * wire cannot reach the row no matter what the caller sent — the projection is
 * the enforcement, not a check the caller could skip.
 */
export function applyAthleteSelfEdit(
  athlete: Athlete,
  patch: AthleteSelfEdit,
): AthleteInput {
  return {
    fullName: athlete.fullName,
    gender: athlete.gender,
    dateOfBirth: athlete.dateOfBirth,
    isCoach: athlete.isCoach,
    coachDescription: patch.coachDescription ?? athlete.coachDescription,
    photoUrls: patch.photoUrls ?? [...athlete.photoUrls],
  };
}

// --- The club roster an athlete sees -------------------------------------

/** A team a club member belongs to, as shown on the club roster. */
export interface ClubMateTeam {
  name: string;
  sportName: string;
  tier: string; // '' = unset
  teamRole: string; // '' = unset
}

/**
 * One member of the club, as seen by another signed-in member. A projection of
 * the same shape family as `PublicAthleteProfile`: it carries the derived age in
 * whole years but never the exact date of birth, never the personal number
 * (the platform primary key), never contact details and never attendance
 * figures. Anything absent here cannot leak across a club.
 *
 * `id` is the athlete's opaque profile token — the identifier that already
 * exists for addressing an athlete without their personal number. It is not a
 * capability on its own: the public profile route serves a profile only when
 * that athlete's `profilePublic` flag is set, which `isPublic` reports.
 */
export interface ClubMateProfile {
  id: string;
  displayName: string;
  isCoach: boolean;
  bio: string;
  photoUrls: string[];
  age: number | null;
  sports: { sportId: SportId; sportName: string }[];
  teams: ClubMateTeam[];
  /** Whether this member's profile is also served on the public link. */
  isPublic: boolean;
}

/** What `toClubMateProfile` needs about one club member. */
export interface ClubMateSource {
  athlete: Athlete;
  sports: { sportId: SportId; sportName: string }[];
  teams: ClubMateTeam[];
}

/**
 * Project a club member down to what their club-mates may see. Kept here rather
 * than in the service so the boundary is one reviewable function with unit
 * tests asserting field-by-field what it does *not* copy.
 */
export function toClubMateProfile(
  source: ClubMateSource,
  now: number = Date.now(),
): ClubMateProfile {
  const { athlete } = source;
  return {
    id: athlete.profileToken,
    displayName: athlete.fullName,
    isCoach: athlete.isCoach,
    bio: athlete.coachDescription,
    photoUrls: [...athlete.photoUrls],
    age: athleteAge(athlete.dateOfBirth, now),
    sports: source.sports.map((sport) => ({ ...sport })),
    teams: source.teams.map((team) => ({ ...team })),
    isPublic: athlete.profilePublic,
  };
}

// --- The invitation challenge -------------------------------------------

/**
 * How long an emailed invitation stays claimable (7 days). Far longer than a
 * login link's 5 minutes on purpose: this is a one-off onboarding step a parent
 * may only get to at the weekend, not an authentication round-trip. The token
 * is still single-use and carries 256 bits of entropy.
 */
export const ATHLETE_INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Client route the emailed invitation points at. Extension-less, so the SPA
 * shell is served for it (see `serveStatic` in `server/index.ts`), which then
 * reads the token from the query string and claims it on an explicit click.
 */
export const ATHLETE_CLAIM_ROUTE = '/claim';

/** Query-string parameter carrying the one-time claim token. */
export const ATHLETE_CLAIM_TOKEN_PARAM = 'token';

/**
 * Build a fresh invitation challenge from an already-computed `tokenHash` (the
 * token generation and keyed hashing are server concerns — see
 * `server/athleteInvite.ts`, mirroring `server/loginLink.ts`). `now` is
 * injectable and sets both `createdAt` and the expiry deadline.
 */
export function createAthleteInviteChallenge(
  id: string,
  personalNumber: string,
  email: string,
  relation: AthleteAccessRelation,
  tokenHash: string,
  invitedByUserId: string | null,
  now: number = Date.now(),
): AthleteInviteChallenge {
  return {
    id,
    personalNumber,
    email,
    relation,
    tokenHash,
    expiresAt: now + ATHLETE_INVITE_TTL_MS,
    consumedAt: null,
    invitedByUserId,
    createdAt: now,
  };
}

/** Whether the invitation has passed its expiry. */
export function isAthleteInviteExpired(
  invite: Pick<AthleteInviteChallenge, 'expiresAt'>,
  now: number = Date.now(),
): boolean {
  return now >= invite.expiresAt;
}

/** Whether the invitation may still be claimed: neither consumed nor expired. */
export function isAthleteInviteUsable(
  invite: Pick<AthleteInviteChallenge, 'expiresAt' | 'consumedAt'>,
  now: number = Date.now(),
): boolean {
  return invite.consumedAt === null && !isAthleteInviteExpired(invite, now);
}

/** Trim a single trailing slash so origin + route never doubles it. */
function trimTrailingSlash(origin: string): string {
  return origin.endsWith('/') ? origin.slice(0, -1) : origin;
}

/**
 * The absolute URL emailed to an athlete or guardian. `origin` is the public app
 * origin — never taken from a request `Host` header (see the login-link flow for
 * why). Pure string building so it stays shared between client and server.
 */
export function buildAthleteClaimUrl(origin: string, token: string): string {
  return `${trimTrailingSlash(origin)}${ATHLETE_CLAIM_ROUTE}?${ATHLETE_CLAIM_TOKEN_PARAM}=${encodeURIComponent(token)}`;
}

/**
 * The `canEdit` right a freshly claimed grant carries. Everyone who legitimately
 * reaches a profile may maintain its self-editable subset — a minor's own login
 * included, since the identity fields are club-locked for every relation alike,
 * so no separate minor tier is needed.
 */
export function defaultCanEdit(_relation: AthleteAccessRelation): boolean {
  return true;
}
